import { gql } from '@apollo/client';
import ApolloClient from 'apollo-client';
import { InMemoryCache } from 'apollo-cache-inmemory';
import { HttpLink, createHttpLink } from 'apollo-link-http';
import env from 'bench-environment';

const graphqlServiceHttpLink = createHttpLink({
  uri: `${env.location.apiRoot}/graphql/query`,
  credentials: 'include',
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

const serverlessGraphqlServiceHttpLink = createHttpLink({
  uri: `${env.location.v2GraphqlRoot}`,
  credentials: 'include',
});

const withV2 = link =>
  HttpLink.split(op => !!op.getContext().useV2, serverlessGraphqlServiceHttpLink, link);

// There is a major slowdown in the cache for Apollo v3 when normalizing big lists. The GL currently processes a HUGE
// amount of data, which leads to long wait times while the normalization is working. To get around this, we are
// intentionally running v2 of Apollo for the GL while the current implementation still exists.
export const apolloV2Client = new ApolloClient({
  link: withV2(graphqlServiceHttpLink),
  cache: new InMemoryCache(),
});

export const GET_ALL_GL_DATA = gql`
  query getGeneralLedger(
    $clientId: ID!
    $fromDate: String!
    $toDate: String!
    $filterZeroEntryLedgers: Boolean
  ) {
    aggregateBalanceSummaries(
      clientId: $clientId
      fromDate: $fromDate
      toDate: $toDate
      filterZeroEntryLedgers: $filterZeroEntryLedgers
    ) {
      id
      ledgerAccount {
        id
        category {
          id
          name
        }
        code
        details
        internalNotes {
          id
          body
        }
        name
        order
        role
        accountLastFourDigits
        financialInstitutionProduct {
          id
          financialInstitution {
            id
            name
          }
          financialInstitutionProductType {
            id
            name
          }
        }
      }
      fromDate
      toDate
      openingBalanceCents
      inPeriodBalanceCents
      closingBalanceCents
      inPeriodDataEntryCount
    }
    ledgerAccountCategoryOrders(clientId: $clientId) {
      id
      ledgerAccountCategory {
        id
        name
      }
      ledgerCategoryOrderingType
    }
    categorizationInconsistencies(clientId: $clientId, fromDate: $fromDate, toDate: $toDate) {
      vendorName
      dataEntryIds
    }
    addOns(clientId: $clientId) {
      id
      name
    }
  }
`;

export const GET_ALL_JOURNAL_ENTRIES = gql`
  query getAllJournalEntries($clientId: ID!, $fromDate: String!, $toDate: String!) {
    tempAllJournalEntries(clientId: $clientId, fromDate: $fromDate, toDate: $toDate) {
      id
      journalEntries {
        id
        dateOfTransaction
        vendorName
        dataEntries {
          id
          amountInCents
          ledgerAccount {
            id
          }
        }
        clientComments {
          id
          body
        }
        internalNotes {
          id
          body
          flagged
        }
        addOn {
          id
          name
        }
        document {
          id
        }
        relatedJE {
          id
        }
        clientCatLedger {
          id
        }
        clientCatResponse
        categorizerMethod
        categorizerLedgerId
        contractorId
        source1099
        isTagged1099
      }
    }
  }
`;

export const GET_BALANCES_DATA = gql`
  query getGeneralLedger(
    $clientId: ID!
    $fromDate: String!
    $toDate: String!
    $filterZeroEntryLedgers: Boolean
  ) {
    aggregateBalanceSummaries(
      clientId: $clientId
      fromDate: $fromDate
      toDate: $toDate
      filterZeroEntryLedgers: $filterZeroEntryLedgers
    ) {
      id
      ledgerAccount {
        id
        category {
          id
          name
        }
        code
        details
        internalNotes {
          id
          body
        }
        name
        order
        role
        accountLastFourDigits
        financialInstitutionProduct {
          id
          financialInstitution {
            id
            name
          }
          financialInstitutionProductType {
            id
            name
          }
        }
      }
      fromDate
      toDate
      openingBalanceCents
      inPeriodBalanceCents
      closingBalanceCents
      inPeriodDataEntryCount
    }
    ledgerAccountCategoryOrders(clientId: $clientId) {
      id
      ledgerAccountCategory {
        id
        name
      }
      ledgerCategoryOrderingType
    }
  }
`;

//todo this isn't utilized at all right now
export const GET_LEDGER_INFO = gql`
  query getLedgerInfo($clientId: ID!) {
    ledgerAccounts(clientId: $clientId) {
      id
      active
      category {
        id
      }
      code
      details
      internalNotes {
        id
        body
      }
      name
      order
      role
    }
    ledgerAccountCategoryOrders(clientId: $clientId) {
      id
      ledgerAccountCategory {
        id
        name
      }
      ledgerCategoryOrderingType
    }
  }
`;

export const GET_CLIENT_BOOKS = gql`
  query getClientBooksData($clientId: ID!, $fromDate: String!, $toDate: String!) {
    clientBooks(clientId: $clientId, fromDate: $fromDate, toDate: $toDate) {
      id
      journalEntries {
        id
        dateOfTransaction
        vendorName
        dataEntries {
          id
          amountInCents
          ledgerAccount {
            id
          }
        }
        clientComments {
          id
          body
        }
        internalNotes {
          id
          body
          flagged
        }
        addOn {
          id
          name
        }
        document {
          id
        }
        relatedJE {
          id
        }
        clientCatLedger {
          id
        }
        clientCatResponse
        categorizerMethod
      }
    }
    categorizationInconsistencies(clientId: $clientId, fromDate: $fromDate, toDate: $toDate) {
      vendorName
      dataEntryIds
    }
    addOns(clientId: $clientId) {
      id
      name
    }
  }
`;

export const GET_BULK_EDIT_FORM_DATA = gql`
  query getBulkEditFormOptions($clientId: ID!) {
    addOns(clientId: $clientId) {
      id
      name
      accountType
    }
    ledgerAccounts(clientId: $clientId) {
      id
      name
      category {
        id
      }
    }
  }
`;

export const GET_CLIENT_COMPANY = gql`
  query getClientCompany($id: ID!) {
    client(id: $id) {
      id
      company
    }
  }
`;

export const GET_BALANCE_SHEET_REPORT = gql`
  query getBalanceSheetReport($clientId: ID!, $date: String!) {
    balanceSheetReport(clientId: $clientId, date: $date)
      @rest(type: "BalanceSheetReport", path: "balance-sheet/json/:clientId/:date") {
      balanceSheetLedgerSummaries {
        balanceInCents
        ledgerAccount {
          id
        }
      }
    }
  }
`;

export const GET_INCOME_STATEMENT_REPORT = gql`
  query getIncomeStatementReport($clientId: ID!, $fromDate: String!, $toDate: String!) {
    incomeStatementReport(clientId: $clientId, fromDate: $fromDate, toDate: $toDate)
      @rest(
        type: "IncomeStatementReport"
        path: "income-statement/json/:clientId/:fromDate/:toDate"
      ) {
      incomeStatementLedgerSummaries {
        ledgerAccount {
          id
        }
        sumTotalInCents
      }
    }
  }
`;

export const BULK_EDIT_DATA_ENTRIES = gql`
  mutation bulkEditDataEntries($updateDataEntriesInput: UpdateDataEntriesInput!) {
    updateDataEntries(updateDataEntriesInput: $updateDataEntriesInput) {
      dataEntries {
        id
      }
    }
  }
`;

export const CONFIRM_CATEGORY_MUTATION = gql`
  mutation confirmCategory($input: ConfirmCategoryInput!) {
    confirmCategory(input: $input) {
      ... on ConfirmCategoryComplete {
        success {
          dataEntryId
          taskId
        }
        failed {
          dataEntryId
          errorMessage
        }
      }
      ... on InvalidInputError {
        code
        message
        inputErrors {
          field
          message
        }
      }
    }
  }
`;

export const RESOLVE_TASKS_MUTATION = gql`
  mutation resolveTasks($input: ResolveTasksInput!) {
    resolveTasks(parameters: $input) {
      ... on ResolveTasksComplete {
        successTasks
        failedTasks {
          taskId
          reason
        }
      }
      ... on InvalidInputError {
        code
        message
        inputErrors {
          field
          message
        }
      }
    }
  }
`;

//v1 query
export const GET_LEDGER_ACCOUNTS = gql`
  query getLedgerAccounts($clientId: ID!) {
    ledgerAccounts(clientId: $clientId) {
      id
      code
    }
  }
`;

//v2 query
export const GET_LEDGER_ACCOUNTS_BY_CLIENT = gql`
  query getLedgerAccountsByClient($clientId: ID!) {
    ledgerAccountsByClient(clientId: $clientId) {
      id
      code
    }
  }
`;

export const GET_CONFIRM_CATEGORY_TASKS = gql`
  query getConfirmCategoryTasks($clientId: ID!) {
    tasks(
      filterBy: {
        clientIds: [$clientId]
        jobTypes: [CONFIRM_CATEGORY]
        taskTypes: [REVIEW_CATEGORY]
      }
    ) {
      tasks {
        id
        status
        resolution
        type
        createdOn
        resolvedOn
        job {
          id
          type
          createdBy {
            fullName
          }
          triggerContext {
            ... on ConfirmCategoryTriggerContext {
              ledgerAccount {
                id
              }
            }
          }
          parameters {
            ... on ConfirmCategoryJobParameters {
              dataEntry {
                id
              }
            }
          }
        }
      }
    }
  }
`;
